// Utility functions for user feedback (sound, haptic, visual)

// Haptic feedback for mobile devices
export const triggerHapticFeedback = (type = 'light') => {
  if ('vibrate' in navigator) {
    switch (type) {
      case 'light':
        navigator.vibrate(50);
        break;
      case 'medium':
        navigator.vibrate(100);
        break;
      case 'heavy':
        navigator.vibrate([100, 50, 100]);
        break;
      case 'success':
        navigator.vibrate([50, 50, 100]);
        break;
      default:
        navigator.vibrate(50);
    }
  }
};

// Audio feedback using Web Audio API
export const playCompletionSound = () => {
  try {
    // Create a simple success sound using Web Audio API
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for the sound
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    // Connect nodes
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure the sound (pleasant chime)
    oscillator.frequency.setValueAtTime(523.25, audioContext.currentTime); // C5
    oscillator.frequency.setValueAtTime(659.25, audioContext.currentTime + 0.1); // E5
    oscillator.frequency.setValueAtTime(783.99, audioContext.currentTime + 0.2); // G5
    
    // Configure volume envelope
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 0.01);
    gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.3);
    
    // Play the sound
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    
  } catch (error) {
    // Fallback: no sound if Web Audio API is not supported
    console.log('Audio feedback not available');
  }
};

// Visual feedback animations
export const createConfettiEffect = (element, options = {}) => {
  const colors = ['#667eea', '#764ba2', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
  const confettiCount = options.count || 30;
  
  for (let i = 0; i < confettiCount; i++) {
    const confetti = document.createElement('div');
    confetti.style.position = 'absolute';
    confetti.style.width = '6px';
    confetti.style.height = '6px';
    confetti.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
    confetti.style.borderRadius = '50%';
    confetti.style.pointerEvents = 'none';
    confetti.style.zIndex = '1000';
    
    // Position relative to the element
    const rect = element.getBoundingClientRect();
    confetti.style.left = `${rect.left + rect.width / 2}px`;
    confetti.style.top = `${rect.top + rect.height / 2}px`;
    
    document.body.appendChild(confetti);
    
    // Animate the confetti
    const angle = (Math.PI * 2 * i) / confettiCount;
    const velocity = (options.velocity || 80) + Math.random() * 40;
    const gravity = options.gravity || 0.8;
    let vx = Math.cos(angle) * velocity;
    let vy = Math.sin(angle) * velocity;
    let x = 0;
    let y = 0;
    
    const animate = () => {
      x += vx;
      y += vy;
      vy += gravity;
      
      confetti.style.transform = `translate(${x}px, ${y}px) rotate(${x * 2}deg)`;
      confetti.style.opacity = Math.max(0, 1 - Math.abs(y) / (options.fadeDistance || 300));

      if (y < (options.fadeDistance || 300) && confetti.style.opacity > 0) {
        requestAnimationFrame(animate);
      } else {
        document.body.removeChild(confetti);
      }
    };
    
    requestAnimationFrame(animate);
  }
};

// Celebration effects for streaks and achievements
export const celebrateAchievement = (element, type = 'completion') => {
  switch (type) {
    case 'completion':
      triggerHapticFeedback('success');
      playCompletionSound();
      break;
    case 'streak':
      triggerHapticFeedback('heavy');
      createConfettiEffect(element, { count: 25, velocity: 70 });
      playCompletionSound();
      break;
    case 'perfect-day':
      triggerHapticFeedback('heavy');
      createConfettiEffect(element, { count: 50, velocity: 100, fadeDistance: 400 });
      // Play a more elaborate sound for perfect day
      setTimeout(() => playCompletionSound(), 200);
      // Create multiple waves of confetti
      setTimeout(() => createConfettiEffect(element, { count: 30, velocity: 80 }), 500);
      break;
    default:
      triggerHapticFeedback('light');
  }
};

// Smooth scroll to element
export const smoothScrollTo = (element, offset = 0) => {
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;
    
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Check if user prefers reduced motion
export const prefersReducedMotion = () => {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Animate element entrance
export const animateElementEntrance = (element, delay = 0) => {
  if (prefersReducedMotion()) return;
  
  element.style.opacity = '0';
  element.style.transform = 'translateY(20px)';
  element.style.transition = 'opacity 0.6s ease, transform 0.6s ease';
  
  setTimeout(() => {
    element.style.opacity = '1';
    element.style.transform = 'translateY(0)';
  }, delay);
};
